/**
 * What the cart costs, as the server states it.
 *
 * This module parses the server's `marketplace:quote_cart` response. It
 * deliberately contains **no fee arithmetic**: every amount here arrives
 * pre-computed from `priceOrder` on the server, which checkout also calls.
 *
 * That is the whole point of the change. The cart used to recompute delivery
 * and service fees locally, in a second implementation that was free to drift
 * from the one that took the money. A cart total and an order total then
 * disagreed, and nothing recorded which one was right. There is now one
 * implementation, and this file only reads it.
 *
 * Two consequences worth keeping in mind when editing here:
 *
 * - The `totals` block is summed server-side from the bills, not recomputed
 *   from a subtotal. Re-deriving it here would reintroduce the second
 *   implementation at a smaller scale.
 * - The bills arrive in the order the server intends the delivery credit to be
 *   spent, largest subtotal first. Checkout must walk them in this order for
 *   the per-kitchen delivery lines to match what the customer was shown.
 */

import { z } from 'zod'

const minor = z.coerce.number().int().nonnegative().safe()

const billSchema = z.object({
 pickup_address: z.string().optional(),
  merchant_profile_id: z.string().min(1),
  item_count: z.coerce.number().int().nonnegative().safe(),
  subtotal_minor: minor,
  delivery_fee_minor: minor,
  service_fee_minor: minor,
  discount_minor: minor,
  total_minor: minor,
  free_delivery_applied: z.coerce.boolean(),
})

const totalsSchema = z.object({
  item_count: z.coerce.number().int().nonnegative().safe(),
  subtotal_minor: minor,
  delivery_fee_minor: minor,
  service_fee_minor: minor,
  discount_minor: minor,
  total_minor: minor,
})

const quoteSchema = z.object({
  delivery_choice: z.string(),
  bills: z.array(billSchema),
  totals: totalsSchema,
})

/** One kitchen's share of the cart, which is one order once you check out. */
export interface KitchenBill {
 pickupAddress?: string
  merchantId: string
  /** Cart lines belonging to this kitchen, in cart order. */
  items: readonly Record<string, unknown>[]
  itemCount: number
  subtotalMinor: number
  deliveryFeeMinor: number
  serviceFeeMinor: number
  /** What this kitchen's order will be charged, before any promo code. */
  totalMinor: number
  /** True when this order took the customer's single delivery credit. */
  freeDeliveryApplied: boolean
  discountMinor?: number
}

export interface CartTotals {
  itemCount: number
  subtotalMinor: number
  deliveryFeeMinor: number
  serviceFeeMinor: number
  discountMinor: number
  totalMinor: number
}

export interface CartQuote {
  bills: readonly KitchenBill[]
  totals: CartTotals
}

export const emptyQuote: CartQuote = {
  bills: [],
  totals: {
    itemCount: 0,
    subtotalMinor: 0,
    deliveryFeeMinor: 0,
    serviceFeeMinor: 0,
    discountMinor: 0,
    totalMinor: 0,
  },
}

/**
 * Attach the cart lines to the bills the server priced.
 *
 * The server reports one row per kitchen and no line detail, because the lines
 * are already in the cart the customer is looking at. Grouping here is by the
 * merchant id the server returned, so a line cannot be attached to the wrong
 * bill.
 */
export const parseCartQuote = (
  payload: unknown,
  items: readonly Record<string, unknown>[],
): CartQuote => {
  const parsed = quoteSchema.parse(payload)
  const keys = ['item_count', 'subtotal_minor', 'delivery_fee_minor', 'service_fee_minor', 'discount_minor', 'total_minor'] as const
  const unique = new Set(parsed.bills.map((bill) => bill.merchant_profile_id))
  if (unique.size !== parsed.bills.length || keys.some((key) => parsed.bills.reduce((sum, bill) => sum + bill[key], 0) !== parsed.totals[key]) ||
    parsed.bills.some((bill) => bill.total_minor !== Math.max(0, bill.subtotal_minor - bill.discount_minor + bill.delivery_fee_minor + bill.service_fee_minor))) {
    throw new Error('The price breakdown does not match the total. Refresh before payment.')
  }
  const byMerchant = new Map<string, Record<string, unknown>[]>()
  for (const item of items) {
    const id = String(item.merchant_profile_id ?? '')
    const bucket = byMerchant.get(id)
    if (bucket) bucket.push(item)
    else byMerchant.set(id, [item])
  }
  const pricedMerchants = new Set(parsed.bills.map((bill) => bill.merchant_profile_id))
  if ([...byMerchant.keys()].some((id) => !pricedMerchants.has(id))) {
    throw new Error('The cart price does not include every kitchen. Refresh the cart before payment.')
  }
  for (const bill of parsed.bills) {
    const quantity = (byMerchant.get(bill.merchant_profile_id) ?? []).reduce((sum, item) => sum + Number(item.quantity ?? 0), 0)
    if (quantity !== bill.item_count) throw new Error('The cart changed while its price was loading. Refresh before payment.')
  }
  const bills = parsed.bills.map((bill): KitchenBill => ({
    merchantId: bill.merchant_profile_id,
    pickupAddress: bill.pickup_address,
    items: byMerchant.get(bill.merchant_profile_id) ?? [],
    itemCount: bill.item_count,
    subtotalMinor: bill.subtotal_minor,
    deliveryFeeMinor: bill.delivery_fee_minor,
    serviceFeeMinor: bill.service_fee_minor,
    totalMinor: bill.total_minor,
    freeDeliveryApplied: bill.free_delivery_applied,
    discountMinor: bill.discount_minor,
  }))
  return {
    bills,
    totals: {
      itemCount: parsed.totals.item_count,
      subtotalMinor: parsed.totals.subtotal_minor,
      deliveryFeeMinor: parsed.totals.delivery_fee_minor,
      serviceFeeMinor: parsed.totals.service_fee_minor,
      discountMinor: parsed.totals.discount_minor,
      totalMinor: parsed.totals.total_minor,
    },
  }
}

export const DEFAULT_KITCHEN_DELIVERY_FEE_MINOR = 0
/** Mirrors the server fallback (₦250). Live quotes always use the ops-published value. */
export const DEFAULT_KITCHEN_SERVICE_FEE_MINOR = 25000

/**
 * A quote for the seeded prototype cart, where no server is reachable.
 *
 * Uses pickup and the default service fee per kitchen. Live pricing always comes from the server.
 */
export const prototypeQuote = (items: readonly Record<string, unknown>[], deliveryChoice = 'self_pickup'): CartQuote => {
  const byMerchant = new Map<string, Record<string, unknown>[]>()
  for (const item of items) {
    const id = String(item.merchant_profile_id ?? '')
    const bucket = byMerchant.get(id)
    if (bucket) bucket.push(item)
    else byMerchant.set(id, [item])
  }
  let subtotalMinor = 0
  let itemCount = 0
  let totalDeliveryFeeMinor = 0
  let totalServiceFeeMinor = 0
  const bills = [...byMerchant.entries()].map(([merchantId, lines]): KitchenBill => {
    const lineSubtotal = lines.reduce(
      (sum, line) => sum + Number(line.unit_amount_minor ?? 0) * Number(line.quantity ?? 0),
      0,
    )
    const lineCount = lines.reduce((sum, line) => sum + Number(line.quantity ?? 0), 0)
    subtotalMinor += lineSubtotal
    itemCount += lineCount
    const deliveryFeeMinor = deliveryChoice === 'self_pickup' ? 0 : DEFAULT_KITCHEN_DELIVERY_FEE_MINOR
    totalDeliveryFeeMinor += deliveryFeeMinor
    const serviceFeeMinor = lineSubtotal > 0 ? DEFAULT_KITCHEN_SERVICE_FEE_MINOR : 0
    totalServiceFeeMinor += serviceFeeMinor
    return {
      merchantId,
      items: lines,
      itemCount: lineCount,
      subtotalMinor: lineSubtotal,
      deliveryFeeMinor,
      serviceFeeMinor,
      totalMinor: lineSubtotal + deliveryFeeMinor + serviceFeeMinor,
      freeDeliveryApplied: false,
    }
  })
  return {
    bills,
    totals: {
      itemCount,
      subtotalMinor,
      deliveryFeeMinor: totalDeliveryFeeMinor,
      serviceFeeMinor: totalServiceFeeMinor,
      discountMinor: 0,
      totalMinor: subtotalMinor + totalDeliveryFeeMinor + totalServiceFeeMinor,
    },
  }
}

